/**
 * How many route changes are in flight right now, app-wide.
 *
 * Every `<Link>` and every `useRouter()` from `@/i18n/navigation` reports here for exactly as long
 * as its own navigation is pending, and `<NavigationProgress>` shows the bar at the top of the page
 * while the count is above zero (docs/10-ux-audit.md · UX-45). A counter rather than a flag, so two
 * overlapping navigations — a link tapped twice, a replace issued during a push — cannot hide each
 * other's bar or end it early.
 *
 * No React in here on purpose: the bar subscribes and writes the DOM itself, so a change is visible
 * whether or not React gets around to flushing a render.
 */
let inFlight = 0;
const listeners = new Set<() => void>();

function emit(): void {
  for (const listener of listeners) listener();
}

/** Marks one navigation as started. Call the returned function once it has settled; extra calls are ignored. */
export function beginNavigation(): () => void {
  inFlight += 1;
  emit();
  let ended = false;
  return () => {
    if (ended) return;
    ended = true;
    inFlight -= 1;
    emit();
  };
}

export function isNavigationPending(): boolean {
  return inFlight > 0;
}

export function subscribeNavigation(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}
